import React, { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import {
  Alert,
  AppBar,
  Box,
  CssBaseline,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material'
import {
  Menu as MenuIcon,
  BugReport,
  Dashboard,
  Devices,
  Assignment,
  People,
  Person,
  Logout,
  ChevronRight,
  ChevronLeft,
  PointOfSale,
  Backup,
  Settings,
} from '@mui/icons-material'
import { useStore } from '../store/authStore'
import { systemUpdateService } from '../services/api'
import { SystemUpdateInfo } from '../types'
import { NAV_ITEMS, AppRole, navItemsForRole } from '../utils/permissions'

const drawerWidth = 240
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0'

const menuIconByPath: Record<string, React.ReactNode> = {
  '/dashboard': <Dashboard />,
  '/devices': <Devices />,
  '/service-visits': <Assignment />,
  '/customers': <People />,
  '/site-settings': <Settings />,
  '/errors': <BugReport />,
  '/users': <Person />,
  '/backup': <Backup />,
}

export default function Layout() {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [updateInfo, setUpdateInfo] = useState<SystemUpdateInfo | null>(null)
  const [showUpdateAlert, setShowUpdateAlert] = useState(true)
  const { user, logout } = useStore()

  const menuItems = navItemsForRole(user?.role).map((item) => ({
    text: item.label,
    path: item.path,
    icon: menuIconByPath[item.path] ?? <Dashboard />,
  }))

  useEffect(() => {
    const loadLatestUpdate = async () => {
      try {
        const response = await systemUpdateService.getLatest(APP_VERSION)
        const payload = response?.data ?? response
        setUpdateInfo(payload?.hasUpdate ? payload : null)
      } catch {
        setUpdateInfo(null)
      }
    }

    loadLatestUpdate()
  }, [])

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
    handleMenuClose()
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    if (isMobile) {
      setMobileOpen(false)
    }
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ 
        background: 'linear-gradient(135deg, #1976d2, #1565c0)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 3,
      }}>
        <PointOfSale sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
            نظام تفعيل POS
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            إدارة الأجهزة والخدمات
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ flexGrow: 1, p: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton 
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  borderRight: `4px solid ${theme.palette.primary.main}`,
                },
                '&.Mui-selected:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <ListItemIcon sx={{ 
                minWidth: 40,
                color: theme.palette.primary.main,
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: '0.95rem',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="caption" color="text.secondary">
          الإصدار الحالي {APP_VERSION}
        </Typography>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          background: 'linear-gradient(135deg, #1976d2, #1565c0)',
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.2)',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2, 
                display: { md: 'none' },
                '&:hover': {
                  backgroundColor: alpha('#fff', 0.1),
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PointOfSale sx={{ fontSize: 28 }} />
              <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 700 }}>
                نظام تفعيل وصيانة أجهزة POS
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ 
              fontWeight: 500,
              display: { xs: 'none', sm: 'block' },
            }}>
              {user?.fullName}
            </Typography>
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{ 
                backgroundColor: alpha('#fff', 0.1),
                '&:hover': {
                  backgroundColor: alpha('#fff', 0.2),
                },
              }}
              aria-controls={anchorEl ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={anchorEl ? 'true' : undefined}
            >
              <Avatar sx={{ 
                width: 36, 
                height: 36,
                backgroundColor: alpha('#fff', 0.2),
                color: 'white',
                fontWeight: 600,
              }}>
                {user?.fullName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 8,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.2))',
                mt: 1.5,
                borderRadius: 2,
                minWidth: 200,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '& .MuiMenuItem-root': {
                  py: 1.5,
                  px: 2,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => handleNavigation('/profile')}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              الملف الشخصي
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              تسجيل الخروج
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ 
          width: { md: drawerWidth }, 
          flexShrink: { md: 0 },
          zIndex: theme.zIndex.drawer,
        }}
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                border: 'none',
                boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
                position: 'fixed',
                height: '100vh',
                top: 0,
                right: 0,
              },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: drawerWidth,
                border: 'none',
                boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
                position: 'fixed',
                height: '100vh',
                top: 0,
                right: 0,
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: '#f8fafc',
          backgroundImage: 'radial-gradient(#e2e8f0 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          overflowX: 'hidden',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Toolbar />
        <Box sx={{ 
          maxWidth: 1400, 
          mx: 'auto',
          width: '100%',
          position: 'relative',
          '& > *': {
            animation: 'fadeIn 0.5s ease-out',
          },
          '@keyframes fadeIn': {
            from: {
              opacity: 0,
              transform: 'translateY(10px)',
            },
            to: {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}>
          <Collapse in={showUpdateAlert && !!updateInfo}>
            {updateInfo && (
              <Alert
                severity={updateInfo.isMandatory ? 'warning' : 'info'}
                onClose={() => setShowUpdateAlert(false)}
                sx={{ mb: 3, borderRadius: 3 }}
              >
                يوجد تحديث جديد للإصدار `{updateInfo.latestVersion}`.
                {` ${updateInfo.title}`}
                {updateInfo.notes ? ` - ${updateInfo.notes}` : ''}
              </Alert>
            )}
          </Collapse>
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}
